import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import type { CeremonyStage, PullOutcome } from "./types";
import { PackSpinSelector } from "./PackSpinSelector";
import { PackRip } from "./PackRip";
import { RevealStage } from "./RevealStage";
import { ProductImage } from "../ProductCard";
import { ListingTitle } from "../ListingTitle";
import { Confetti } from "../fx/Confetti";
import { Particles } from "../fx/Particles";
import { CountUp } from "../fx/CountUp";
import { Button, Spinner } from "../ui";
import { rm } from "../../lib/format";
import { sfx } from "../../lib/sfx";
import { toast } from "../../stores/toast";
import { EASE, rarityClass, rarityIntensity } from "../../lib/motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { useAuth } from "../../stores/auth";

interface CeremonyProps {
  open: boolean;
  packId: string;
  packName: string;
  packImage: string | null;
  foilLabel?: string | null;
  foilColor?: string | null;
  /** Number of pulls the collector is paying for. */
  count: number;
  /** Performs the real purchase + draw once the pack is chosen. */
  onCommit: (count: number) => Promise<PullOutcome[]>;
  onClose: () => void;
  /** Restart the whole ceremony with the same pack. */
  onOpenAnother?: () => void;
}

/**
 * Full-screen pack-opening ceremony: pick a pack → tear the foil → cards come
 * out face-down → flip them over → haul summary.
 */
export function PackOpeningCeremony({
  open,
  packId,
  packName,
  packImage,
  foilLabel,
  foilColor,
  count,
  onCommit,
  onClose,
  onOpenAnother,
}: CeremonyProps) {
  const [stage, setStage] = useState<CeremonyStage>("choose");
  const [pulls, setPulls] = useState<PullOutcome[] | null>(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bulkStartedRef = useRef(false);
  const skipChoose = count > 1;

  useBodyLock(open);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
  }, [stage]);

  useEffect(() => {
    if (open) {
      setStage("choose");
      setPulls(null);
      setBusy(false);
      bulkStartedRef.current = false;
      scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [open, packId, count]);

  const confirm = useCallback(async () => {
    setBusy(true);
    try {
      const result = await onCommit(count);
      if (!result.length) {
        // The purchase went through but the server returned nothing to show —
        // never advance into the rip/reveal stages with no cards to draw,
        // that's what left the ceremony rendering an empty screen before.
        toast("error", "Something went wrong opening the pack — try again.");
        onClose();
        return;
      }
      setPulls(result);
      setStage("rip");
    } catch {
      // The caller surfaces the toast; drop back so nothing is left mid-ritual.
      onClose();
    } finally {
      setBusy(false);
    }
  }, [count, onCommit, onClose]);

  // Bulk opens skip pack selection — draw immediately, then rip.
  useEffect(() => {
    if (!open || !skipChoose || bulkStartedRef.current) return;
    bulkStartedRef.current = true;
    void confirm();
  }, [open, skipChoose, confirm]);

  const goReveal = useCallback(() => setStage("reveal"), []);
  const goSummary = useCallback(() => setStage("summary"), []);

  /** Shared fade timing between ceremony beats (rip → flip → haul). */
  const STAGE_FADE = 0.38;

  const stageMotion = {
    initial: { opacity: 0, pointerEvents: "none" as const },
    animate: { opacity: 1, pointerEvents: "auto" as const },
    exit: { opacity: 0, pointerEvents: "none" as const },
  };

  // Escape only exits before the draw is committed — no bailing mid-reveal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && (stage === "choose" || stage === "summary")) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, stage, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={scrollRef}
          role="dialog"
          aria-modal="true"
          aria-label="Pack opening"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-noise fixed inset-0 z-[90] flex flex-col overflow-y-auto bg-brand-dark/98 bg-mesh-deep"
        >
          <Particles count={22} />

          {(stage === "choose" || stage === "summary") && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="fixed right-4 top-4 z-20 rounded-full bg-white/5 p-2.5 text-white/60 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={18} />
            </button>
          )}

          <div className="relative z-10 flex min-h-[100dvh] w-full flex-1 flex-col">
            <div className="relative min-h-[100dvh] w-full flex-1">
            <AnimatePresence initial={false}>
              {stage === "choose" && !skipChoose && (
                <motion.div
                  key="choose"
                  className="absolute inset-0 z-20 flex min-h-[100dvh] w-full flex-col overflow-y-auto"
                  {...stageMotion}
                  exit={{ opacity: 0, scale: 0.97, pointerEvents: "none" }}
                  transition={{ duration: STAGE_FADE, ease: EASE.outExpo }}
                >
                  <PackSpinSelector
                    packId={packId}
                    packName={packName}
                    packImage={packImage}
                    foilLabel={foilLabel}
                    foilColor={foilColor}
                    busy={busy}
                    onConfirm={() => void confirm()}
                    onCancel={onClose}
                  />
                </motion.div>
              )}

              {stage === "choose" && skipChoose && busy && (
                <motion.div
                  key="sealing"
                  className="absolute inset-0 z-20 flex min-h-[100dvh] w-full flex-col items-center justify-center"
                  {...stageMotion}
                  transition={{ duration: STAGE_FADE, ease: EASE.outExpo }}
                >
                  <Spinner className="h-10 w-10 text-brand-pink" />
                  <p className="mt-4 font-display text-sm font-bold text-white/80">Sealing your roll…</p>
                  <p className="mt-1 text-[11px] text-white/40">
                    {count} pack{count > 1 ? "s" : ""} — get ready to rip
                  </p>
                </motion.div>
              )}

              {stage === "rip" && pulls && (
                <motion.div
                  key="rip"
                  className="absolute inset-0 z-30 flex min-h-[100dvh] w-full flex-col overflow-y-auto overscroll-contain"
                  {...stageMotion}
                  initial={{ opacity: 0, scale: 1.03, pointerEvents: "none" }}
                  animate={{ opacity: 1, scale: 1, pointerEvents: "auto" }}
                  exit={{ opacity: 0, scale: 0.96, pointerEvents: "none" }}
                  transition={{ duration: STAGE_FADE, ease: EASE.outExpo }}
                >
                  <PackRip
                    packId={packId}
                    packName={packName}
                    packImage={packImage}
                    foilLabel={foilLabel}
                    foilColor={foilColor}
                    packCount={count}
                    onTorn={goReveal}
                  />
                </motion.div>
              )}

              {stage === "reveal" && pulls && (
                <motion.div
                  key="reveal"
                  className="absolute inset-0 z-40 flex min-h-[100dvh] w-full flex-col overflow-y-auto"
                  {...stageMotion}
                  initial={{ opacity: 0, scale: 0.97, pointerEvents: "none" }}
                  animate={{ opacity: 1, scale: 1, pointerEvents: "auto" }}
                  exit={{ opacity: 0, scale: 0.96, y: -12, pointerEvents: "none" }}
                  transition={{ duration: STAGE_FADE, ease: EASE.outExpo }}
                >
                  <RevealStage pulls={pulls} onDone={goSummary} />
                </motion.div>
              )}

              {stage === "summary" && pulls && (
                <motion.div
                  key="summary"
                  className="absolute inset-0 z-50 flex min-h-[100dvh] w-full flex-col overflow-y-auto"
                  {...stageMotion}
                  initial={{ opacity: 0, y: 20, pointerEvents: "none" }}
                  animate={{ opacity: 1, y: 0, pointerEvents: "auto" }}
                  exit={{ opacity: 0, pointerEvents: "none" }}
                  transition={{ duration: STAGE_FADE, ease: EASE.outExpo }}
                >
                  <HaulSummary
                    pulls={pulls}
                    packName={packName}
                    onClose={onClose}
                    onOpenAnother={onOpenAnother}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function HaulSummary({
  pulls,
  packName,
  onClose,
  onOpenAnother,
}: {
  pulls: PullOutcome[];
  packName: string;
  onClose: () => void;
  onOpenAnother?: () => void;
}) {
  const { moduleEnabled } = useAuth();
  const collectionOn = moduleEnabled("collection");
  const total = pulls.reduce((sum, p) => sum + (p.estValue ?? 0), 0);
  const best = pulls.reduce<PullOutcome | undefined>(
    (top, p) => (!top || rarityIntensity(p.tierName) > rarityIntensity(top.tierName) ? p : top),
    undefined,
  );
  const celebrate = !!best && rarityIntensity(best.tierName) >= 2;

  useEffect(() => {
    if (celebrate) sfx.win();
  }, [celebrate]);

  if (!best) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 py-6 text-center">
        <p className="text-sm text-white/60">No cards to show.</p>
        <Button onClick={onClose}>Close</Button>
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-full max-w-3xl px-4 py-10">
      {celebrate && <Confetti fireKey="haul" intensity={3} colors={[best.tierColor]} />}

      <div className="text-center">
        <p className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-brand-pink">Your haul</p>
        <h2 className="mt-2 font-display text-2xl font-extrabold lg:text-3xl">
          <span className="text-gradient-holo">{pulls.length}</span> card{pulls.length > 1 ? "s" : ""} secured
        </h2>
        <p className="mt-1 text-xs text-white/45">from {packName}</p>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2.5">
        <Stat label="Cards" value={<CountUp value={pulls.length} />} />
        <Stat label="Est. value" value={<CountUp value={total} format={(n) => rm(n)} />} />
        <Stat label="Best hit" value={<span style={{ color: best.tierColor }}>{best.tierName}</span>} />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {pulls.map((pull, i) => (
          <motion.div
            key={pull.pullId}
            initial={{ opacity: 0, y: 24, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ delay: 0.08 * i, duration: 0.45, ease: EASE.outExpo }}
            className={`overflow-hidden rounded-2xl border border-white/5 bg-surface ${rarityClass(pull.tierName)} rarity-aura`}
            style={{ ["--rarity-color" as string]: pull.tierColor }}
          >
            <ProductImage src={pull.productImage} alt={pull.productName} className="aspect-[3/4] w-full" />
            <div className="space-y-1 p-2.5">
              <span
                className="inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                style={{ color: pull.tierColor, background: `${pull.tierColor}22` }}
              >
                {pull.tierName}
              </span>
              <ListingTitle className="text-[11px] leading-4">{pull.productName}</ListingTitle>
              {pull.estValue != null && (
                <p className="text-[10px] text-white/40">est. {rm(pull.estValue)}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
        {collectionOn && (
          <Link to="/inventory" className="sm:w-56">
            <Button className="w-full">Send to my vault</Button>
          </Link>
        )}
        {onOpenAnother && (
          <Button variant="outline" className="sm:w-44" onClick={onOpenAnother}>
            Open another
          </Button>
        )}
        <Button variant="ghost" className="sm:w-32" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="glass-panel rounded-2xl px-3 py-3 text-center">
      <p className="font-display text-sm font-bold lg:text-base">{value}</p>
      <p className="mt-0.5 text-[9px] uppercase tracking-widest text-white/40">{label}</p>
    </div>
  );
}
